import { presentResults, publicRequest, readAsset, readData, retrying, type PublicPageDocument } from 'features/public-pages/public/publicApi';
import type { Interaction, LiveAnswer, QueryWindow, Report, Results } from '../types';

/**
 * An HTML page opened by public link is the page every reader sees (docs/architecture/pages.md, 4.5): the same workspace and page view,
 * read-only. Only where they read differs: the page, its data, its images and its live changes come from the link's anonymous routes
 * (`/api/public/pages/<token>/...`), whose bodies are the positive allowlists of `app/shared/public_page_allowlist.py`. The workspace
 * names the page `LINK_ID`; the token itself stays in this module and is never put in a request the workspace makes.
 */
export const LINK_ID = 'public-link';

let shown: { token: string; page: PublicPageDocument } | null = null;
/** The page the workspace is drawing and the link it came from; the reads below answer for it (only a workspace that names `LINK_ID` asks). */
export const showHtmlLink = (token: string, page: PublicPageDocument) => { shown = { token, page }; };
export const isHtmlLink = (id: unknown) => id === LINK_ID && shown !== null;

/** The page as the workspace holds an HTML page: a reader's projection without queries (`scope: 'pages'`), whose data context is the link's. */
export const linkReport = (): Report => {
    const { page } = shown!;
    return {
        id: LINK_ID, ownerId: '', name: page.name, version: page.version, accessLevel: 'View', updatedAt: '',
        scope: 'pages', dataContext: page.dataContext,
        document: { formatVersion: 1, name: page.name, pages: [{
            id: LINK_ID, name: page.name, html: page.html, visuals: page.visuals,
            data: page.datasets.map(({ id, title, paging }) => ({ id, title, paging })),
        }] },
    };
};

/** The page's data (or one dataset's window) under a selection, waiting out the server's throttling. */
export async function readLinkData(state: { interaction?: Interaction | null; window?: QueryWindow; targets?: string[] }, target?: string, signal?: AbortSignal) {
    const stop = signal ?? new AbortController().signal;
    const { context, results } = await retrying(() => readData(shown!.token, state, target, stop), stop);
    return { context, results: presentResults(results) as Results };
}

export const readLinkAsset = (id: string) => readAsset(shown!.token, id);

/** The net change from the version the page shows to the link's head. */
export const readLinkChanges = (after: number, signal?: AbortSignal) =>
    publicRequest<LiveAnswer>(shown!.token, `/live?after=${encodeURIComponent(after)}`, signal);
